import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  ButtonLink,
  ButtonRound,
  CloseIcon,
  DocAddIcon,
  InputDragDrop,
  InputText,
  ModalForm,
  Select,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { formatFileSize } from "@helpers/documentFile";
import { labelWithHint, searchLabelAndHint } from "@components/OptionWithHint";
import { focusFirstError } from "../../Common/FormPage";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// Cartão do arquivo escolhido: nome (abre a prévia), tamanho e remover
const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.N100};
  border-radius: 10px;
`;

const FileName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  button {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// A área da lib tem 250px de altura mínima: com ela o modal passava da tela num
// notebook (~650px úteis), como no criar comunicado
const CompactDrop = styled.div`
  > div {
    min-height: 150px;
    padding: 20px 40px;
  }
`;

const DropIcon = styled(DocAddIcon)`
  width: 40px;
  height: 40px;
`;

const IDS = { name: "document-name", shared: "document-shared", file: "document-file" };

// varchar(255) no banco
const NAME_MAX = 255;

/** Nome do arquivo sem a extensão, para sugerir o nome do documento. */
const baseName = (fileName) => String(fileName || "").replace(/\.[^.]+$/, "");

/**
 * Enviar documento (sem `doc`) ou editar o nome e o compartilhamento de um (com
 * `doc`). O PUT do back não troca o arquivo, então ele só entra no envio. Quem entra
 * no compartilhamento recebe o e-mail "Novo documento enviado!" (o back enfileira).
 * Montado só aberto.
 *
 * Props:
 * - doc: documento em edição ({ id, filename, sharedUsers })
 * - userOptions: colaboradores para o "Compartilhar com" ({ value, label, hint }); null carregando
 * - onClose
 * - onSaved: depois de salvar (recarregar a lista)
 */
export default function DocumentFormModal({ doc, userOptions, onClose, onSaved }) {
  const documentsService = useService("documents");
  const editing = !!doc;
  const [name, setName] = useState(doc?.filename || "");
  const [shared, setShared] = useState(() => (doc?.sharedUsers || []).map((u) => u.id));
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  // Quem já tem o documento e não está na lista de colaboradores (ex.: um ADM)
  // continua como opção: sem isso, salvar tiraria a pessoa do compartilhamento
  const options = useMemo(() => {
    const list = userOptions || [];
    const known = new Set(list.map((o) => o.value));
    const extra = (doc?.sharedUsers || [])
      .filter((u) => !known.has(u.id))
      .map((u) => ({ value: u.id, label: u.name || `Colaborador #${u.id}`, hint: "" }));
    return [...list, ...extra];
  }, [userOptions, doc]);

  function close() {
    if (!saving) onClose();
  }

  function clearError(id) {
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  }

  function chooseFile(accepted) {
    const chosen = accepted[0];
    if (!chosen) return;
    setFile(chosen);
    clearError(IDS.file);
    if (!name.trim()) {
      setName(baseName(chosen.name).slice(0, NAME_MAX));
      clearError(IDS.name);
    }
  }

  async function save() {
    const value = name.trim();
    const found = {};
    if (!value) found[IDS.name] = "Informe o nome do documento.";
    if (!editing && !file) found[IDS.file] = "Escolha o arquivo.";
    if (Object.keys(found).length) {
      setErrors(found);
      focusFirstError([IDS.name, IDS.file].filter((id) => found[id]));
      return;
    }

    setSaving(true);
    let result;
    if (editing) {
      // Em erro, o update do service devolve vazio (sem toast). sharedWith vazio tira todos.
      result = await documentsService.update({ filename: value, sharedWith: shared.join(",") }, doc.id);
      if (!result || result.error) {
        setSaving(false);
        toast.error("Erro ao atualizar o documento");
        return;
      }
    } else {
      // Em erro, o service já mostra o toast e devolve { error: true }
      // Mesmo FormData da tela antiga: "filename", "file" e os ids em "sharedWith"
      const formData = new FormData();
      formData.append("filename", value);
      formData.append("file", file);
      formData.append("sharedWith", shared.join(","));
      result = await documentsService.uploadDocument(formData);
    }
    setSaving(false);
    if (!result || result.error) return;

    if (editing) toast.success("Documento atualizado");
    else toast.success(shared.length ? "Documento enviado. Os colaboradores escolhidos recebem um e-mail." : "Documento enviado");
    onSaved();
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title={editing ? "Editar documento" : "Enviar documento"}
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: editing ? "Salvar documento" : "Enviar documento", action: save, variant: "primary", isDisabled: saving },
      ]}
    >
      <Body>
        <InputText
          $idForm={IDS.name}
          $label="Nome do documento"
          $variant="default"
          $isRequired
          $errors={errors[IDS.name]}
          maxLength={NAME_MAX}
          placeholder="Ex.: Contrato de prestação de serviços"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            clearError(IDS.name);
          }}
        />
        <Field>
          <Select
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            $idForm={IDS.shared}
            $label="Compartilhar com"
            $options={options}
            $textNoOptions="Nenhum colaborador encontrado"
            isLoading={userOptions === null}
            placeholder="Selecione os colaboradores"
            formatOptionLabel={labelWithHint}
            filterOption={searchLabelAndHint}
            value={options.filter((o) => shared.includes(o.value))}
            onChange={(selected) => setShared((selected || []).map((o) => o.value))}
          />
          <Text
            $text={
              editing
                ? "Quem você incluir recebe um e-mail. Quem você tirar deixa de ver o documento."
                : "Quem você escolher vê o documento em Documentos e recebe um e-mail."
            }
            $variant="caption_medium"
            $color="N400"
          />
        </Field>
        {editing ? null : (
          <Field id={IDS.file}>
            {file ? (
              <FileItem>
                <FileName>
                  <ButtonLink
                    $text={file.name}
                    $action={() => window.open(preview, "_blank", "noopener")}
                    $noPadding
                    $noWidth
                  />
                  <Text $text={formatFileSize(file.size)} $variant="caption_medium" $color="N400" />
                </FileName>
                <ButtonRound
                  title="Remover arquivo"
                  aria-label="Remover arquivo"
                  $variant="outline"
                  $icon={<CloseIcon />}
                  $noWidth
                  $action={() => setFile(null)}
                />
              </FileItem>
            ) : (
              <CompactDrop>
                <InputDragDrop
                  $icon={<DropIcon />}
                  $message="Arraste o arquivo ou <strong>clique para escolher</strong>"
                  onDrop={chooseFile}
                />
              </CompactDrop>
            )}
            {errors[IDS.file] ? <Text $text={errors[IDS.file]} $variant="caption_medium" $color="E400" /> : null}
          </Field>
        )}
      </Body>
    </ModalForm>
  );
}
